import { useEffect, useRef } from 'react';

import { TableCell, TableRow } from './ui/table';

interface DataTableLoadMoreProps {
  colSpan: number;
  rowCount: number;
  hasMore: boolean;
  loadingMore: boolean;
  onReachEnd: () => void;
  /** What the last row says once nothing is left. */
  endText: string;
}

/**
 * The last row of a table that loads on scroll. When it scrolls into view the
 * next page is asked for; it is the one place that decides, so a caller only
 * says whether there is more.
 */
export function DataTableLoadMore({
  colSpan,
  rowCount,
  hasMore,
  loadingMore,
  onReachEnd,
  endText,
}: DataTableLoadMoreProps) {
  const sentinel = useRef<HTMLTableCellElement>(null);
  const reachEnd = useRef(onReachEnd);
  reachEnd.current = onReachEnd;

  // Depending on rowCount re-creates the observer after each page lands: an
  // observer only reports a change, so a page too short to push the sentinel
  // out of view would otherwise never ask for the next one.
  // biome-ignore lint/correctness/useExhaustiveDependencies: rowCount is the trigger, not a value read.
  useEffect(() => {
    const element = sentinel.current;
    if (!element || !hasMore || loadingMore) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) reachEnd.current();
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [hasMore, loadingMore, rowCount]);

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell
        ref={sentinel}
        colSpan={colSpan}
        className="py-3 text-center text-xs text-muted-foreground"
      >
        {loadingMore ? 'Loading more…' : hasMore ? '' : endText}
      </TableCell>
    </TableRow>
  );
}
